import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../utils/axiosConfig';
import { useAuth } from './AuthContext';

export interface FavoriSayfa {
  id: number;
  sayfaKey: string;
  sayfaAdi: string;
  sayfaUrl: string;
  ikon?: string;
  renk?: string;
  sira: number;
}

interface FavoriSayfalarContextType {
  favoriler: FavoriSayfa[];
  loading: boolean;
  isFavori: (sayfaKey: string) => boolean;
  ekle: (sayfa: Omit<FavoriSayfa, 'id' | 'sira'>) => Promise<void>;
  sil: (id: number) => Promise<void>;
  siralaGuncelle: (yeniSira: FavoriSayfa[]) => Promise<void>;
}

const FavoriSayfalarContext = createContext<FavoriSayfalarContextType | null>(null);

export const FavoriSayfalarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [favoriler, setFavoriler] = useState<FavoriSayfa[]>([]);
  const [loading, setLoading] = useState(false);
  const fetchedRef = useRef(false);

  const fetchFavoriler = useCallback(async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await axiosInstance.get('/api/KullaniciFavoriSayfalar');
      setFavoriler(res.data);
      fetchedRef.current = true;
    } catch {
      // sessiz hata
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (user && !fetchedRef.current) {
      fetchFavoriler();
    }
    if (!user) {
      setFavoriler([]);
      fetchedRef.current = false;
    }
  }, [user, fetchFavoriler]);

  const isFavori = useCallback(
    (sayfaKey: string) => favoriler.some(f => f.sayfaKey === sayfaKey),
    [favoriler]
  );

  const ekle = useCallback(async (sayfa: Omit<FavoriSayfa, 'id' | 'sira'>) => {
    const res = await axiosInstance.post('/api/KullaniciFavoriSayfalar', {
      sayfaKey: sayfa.sayfaKey,
      sayfaAdi: sayfa.sayfaAdi,
      sayfaUrl: sayfa.sayfaUrl,
      ikon: sayfa.ikon,
      renk: sayfa.renk,
    });
    const yeni: FavoriSayfa = { ...sayfa, id: res.data.id, sira: res.data.sira };
    setFavoriler(prev => [...prev, yeni].sort((a, b) => a.sira - b.sira));
  }, []);

  const sil = useCallback(async (id: number) => {
    await axiosInstance.post(`/api/KullaniciFavoriSayfalar/delete/${id}`);
    setFavoriler(prev => prev.filter(f => f.id !== id));
  }, []);

  const siralaGuncelle = useCallback(async (yeniSira: FavoriSayfa[]) => {
    setFavoriler(yeniSira);
    await axiosInstance.post('/api/KullaniciFavoriSayfalar/sirala', yeniSira.map(f => f.id));
  }, []);

  return (
    <FavoriSayfalarContext.Provider value={{ favoriler, loading, isFavori, ekle, sil, siralaGuncelle }}>
      {children}
    </FavoriSayfalarContext.Provider>
  );
};

export const useFavoriSayfalar = () => {
  const ctx = useContext(FavoriSayfalarContext);
  if (!ctx) throw new Error('useFavoriSayfalar must be used within FavoriSayfalarProvider');
  return ctx;
};
